import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const ProjectManagement = React.lazy(() => import('./ProjectManagement'));
const WorkloadAnalysis = React.lazy(() => import('./WorkloadAnalysis'));
const ProjectBoard = React.lazy(() => import('../Common/ProjectBoard'));
const ProductManagement = React.lazy(() => import('./ProductManagement'));

type MgmtTab = 'projects' | 'workload' | 'board' | 'products';

const TABS: { key: MgmtTab; label: string }[] = [
  { key: 'projects', label: 'Projects' },
  { key: 'products', label: 'Products' },
  { key: 'workload', label: 'Workload' },
  { key: 'board', label: 'Board' },
];

export default function AdminManagementHub() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as MgmtTab | null;
  const [activeTab, setActiveTab] = useState<MgmtTab>(tabParam && TABS.some(t => t.key === tabParam) ? tabParam : 'projects');

  const handleTabChange = (tab: MgmtTab) => {
    setActiveTab(tab);
    setSearchParams({ tab }, { replace: true });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-3">
        <h1 className="text-xl font-semibold text-gray-900">Management</h1>
        <p className="text-sm text-gray-400 mt-0.5">Projects, workload, board and products</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 px-6">
        <div className="flex gap-0 -mb-px">
          {TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => handleTabChange(tab.key)}
              className={`px-4 py-2.5 text-xs font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <React.Suspense fallback={
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" />
        </div>
      }>
        {activeTab === 'projects' && <ProjectManagement />}
        {activeTab === 'workload' && <WorkloadAnalysis />}
        {activeTab === 'board' && <ProjectBoard />}
        {activeTab === 'products' && <ProductManagement />}
      </React.Suspense>
    </div>
  );
}
